import React from 'react';

import { Bone } from '@lightspeed/flame/Bone/Bone';
import { Box, Flex } from '@lightspeed/flame/Core';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { IconMail } from '@lightspeed/flame/Icon/Mail';
import { IconPayments } from '@lightspeed/flame/Icon/Payments';
import { IconPrinters } from '@lightspeed/flame/Icon/Printers';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Heading3, Text, TextLink } from '@lightspeed/flame/Text';

import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';

import {
  DueAmountFormats,
  usePaymentTermsInputContext,
} from '../contexts/CreditStatementTermsContext';
import { OpenModalTypes } from '../types';
import {
  getPaymentLinkAmountLimits,
  hasCrossedPaymentLinkMaxMinAmount,
  printCreditStatement,
} from '../utils';
import { StyledButton, StyledFlex, StyledSwitch, StyledText } from './Common.style';
import { CREDIT_ACCOUNT_STATEMENT_FORM_URL } from './PaymentTermsContainer';

export const lspMerchantBannerText =
  'Customers can now pay online directly through their account statements.';
export const nonLspMerchantBannerText =
  'Allow customers to pay online directly through their account statements.';
export const LSP_PAYMENT_LINK_DOC_URL =
  'https://retail-support.lightspeedhq.com/hc/{{locale}}/articles/25958383346971';

export type PaymentTermsInputProps = {
  openModalAfterLangSelection: (modal: OpenModalTypes) => void;
  isLoadingHasLSP: boolean;
};

const localCurrencySymbol = window.merchantos.locale?.currency_symbol || '$';

const OverPayLinkLimitAlert = () => {
  return (
    <StyledFlex
      data-test="above-pay-limit-alert"
      color="#EA891E"
      padding="6px"
      style={{ gap: '6px', borderBottom: '1px solid #DDDDDD' }}
      flexDirection="row"
    >
      <Flex flexDirection="column" alignItems="flex-start">
        <IconWarning size="16px" color="#EA891E" />
      </Flex>
      <Text fontSize="12px" fontWeight={300}>
        Lightspeed payment links have a limit of{' '}
        <var>
          {localCurrencySymbol}
          {getPaymentLinkAmountLimits().max.toLocaleString()}
        </var>
        . If you need to collect more than{' '}
        <var>
          {localCurrencySymbol}
          {getPaymentLinkAmountLimits().max.toLocaleString()}
        </var>{' '}
        by payment link, you can generate multiple account statements.
      </Text>
    </StyledFlex>
  );
};

const BelowPayLinkLimitAlert = () => {
  return (
    <StyledFlex
      data-test="below-pay-limit-alert"
      color="#EA891E"
      padding="6px"
      style={{ gap: '6px', borderBottom: '1px solid #DDDDDD' }}
      flexDirection="row"
    >
      <Flex flexDirection="column" alignItems="flex-start">
        <IconWarning size="16px" color="#EA891E" />
      </Flex>
      <Text fontSize="12px" fontWeight={300}>
        Amount due must be more than <var>{localCurrencySymbol}0.50</var> to create a payment link.
      </Text>
    </StyledFlex>
  );
};

const LoadingBones = () => {
  return (
    <StyledFlex flexDirection="row" data-test="credit-statement-banner-loading">
      <Flex width="70%" style={{ gap: '10px' }} alignItems="center">
        <Box width="10%">
          <Bone height={6} width={6} />
        </Box>
        <Box width="90%">
          <Bone height={8} />
        </Box>
      </Flex>
      <Flex
        flexDirection="column"
        width="30%"
        style={{ gap: '10px' }}
        paddingLeft={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box width="100%">
          <Bone height={3} />
        </Box>
        <Box width="100%">
          <Bone height={3} />
        </Box>
      </Flex>
    </StyledFlex>
  );
};

export default function PaymentTermsInput({
  openModalAfterLangSelection,
  isLoadingHasLSP,
}: PaymentTermsInputProps) {
  const {
    globalState: {
      printDocumentLanguage,
      startDate,
      endDate,
      hasLightSpeedPayments,
      isFetchingOwedAmount,
      includePaymentLink,
    },
    constantProps: {
      employeeId,
      registerId,
      shopId,
      creditAccount: { id: creditAccountId },
      billingIncludePayLink,
    },
    setGlobalState,
    getDueAmount,
    getDueDate,
  } = usePaymentTermsInputContext();

  const statementPeriodBalanceUrl = `${CREDIT_ACCOUNT_STATEMENT_FORM_URL}&credit_account_id=${creditAccountId}&start=${startDate}&end=${endDate}`;

  const REMOTE_PAY_DOCS_LINK = useLocalizedLink(LSP_PAYMENT_LINK_DOC_URL);

  const handleEmailClick = () => {
    // If print document language is not set, show language modal and after selection, show email modal
    if (printDocumentLanguage === '') {
      setGlobalState((prevState) => ({ ...prevState, openModal: 'printDocumentLanguage' }));
      openModalAfterLangSelection('email');
    } else {
      setGlobalState((prevState) => ({ ...prevState, openModal: 'email' }));
    }
  };

  const handlePrintClick = async () => {
    // If print document language is not set, show language modal, and after language selection, no other modal should open
    if (printDocumentLanguage === '') {
      setGlobalState((prevState) => ({ ...prevState, openModal: 'printDocumentLanguage' }));
      openModalAfterLangSelection('none');
    } else {
      const dueAmountInCents = getDueAmount(DueAmountFormats.CENTS);
      const dueDate = getDueDate();

      await printCreditStatement({
        balanceCheckUrl: statementPeriodBalanceUrl,
        creditAccountId,
        language: printDocumentLanguage,
        hasLightSpeedPayments,
        employeeId,
        registerId,
        shopId,
        dueAmountInCents,
        dueDate,
        includePaymentLink,
      });
    }
  };

  React.useEffect(() => {
    setGlobalState((prevState) => ({ ...prevState, includePaymentLink: billingIncludePayLink }));
  }, [billingIncludePayLink, setGlobalState]);

  const handlePayLinkToggle: React.FormEventHandler<HTMLInputElement> = (event): void => {
    const includePaymentLink = event.currentTarget.checked;
    setGlobalState((prevState) => ({
      ...prevState,
      includePaymentLink,
    }));
  };

  const { belowMinAmount, overMaxAmount } = hasCrossedPaymentLinkMaxMinAmount(
    getDueAmount(DueAmountFormats.CENTS)
  );

  const showOverPayLinkLimitAlert = !isFetchingOwedAmount && overMaxAmount && hasLightSpeedPayments;
  const showBelowPayLinkLimitAlert =
    !isFetchingOwedAmount && belowMinAmount && hasLightSpeedPayments;

  return (
    <>
      {showOverPayLinkLimitAlert ? <OverPayLinkLimitAlert /> : null}
      {showBelowPayLinkLimitAlert ? <BelowPayLinkLimitAlert /> : null}
      {isLoadingHasLSP ? (
        <LoadingBones />
      ) : (
        <>
          <Flex
            style={{
              height: '10px',
              backgroundColor: '#fff',
              marginRight: '-1px',
              paddingRight: '1px',
            }}
          />
          <Heading3 style={{ lineHeight: 'normal' }}>
            Add payment link to account statement
            {hasLightSpeedPayments ? (
              <div style={{ float: 'right', padding: '0px', marginTop: '-3px' }}>
                <StyledSwitch
                  onChange={handlePayLinkToggle}
                  data-test="payment-link-toggle"
                  defaultChecked={billingIncludePayLink}
                />
              </div>
            ) : null}
          </Heading3>
          <StyledFlex style={{ gap: '6px' }} justifyContent="space-between">
            <Flex flexDirection="row" style={{ gap: '10px', marginRight: '6px' }}>
              <Flex alignItems="center" pl="6px">
                <IconPayments size="28px" />
              </Flex>
              <Flex flexDirection="column" justifyContent="center" fontSize="14px">
                <StyledText
                  data-test={`credit-statement-banner-text-${
                    hasLightSpeedPayments ? '' : 'non-'
                  }LSP`}
                  style={{ whiteSpace: window.innerWidth > 1400 ? 'nowrap' : 'unset' }}
                >
                  {hasLightSpeedPayments ? lspMerchantBannerText : nonLspMerchantBannerText}
                </StyledText>
                <TextLink
                  data-test="lightspeed-pay-link-learn-more-doc"
                  fontWeight="700"
                  mt="4px"
                  target="_blank"
                  href={REMOTE_PAY_DOCS_LINK}
                  width="fit-content"
                >
                  Learn more about Lightspeed payment links <IconExternalLink color="#2E61DE" />
                </TextLink>
              </Flex>
            </Flex>
          </StyledFlex>
          <Flex
            style={{
              height: '10px',
              backgroundColor: 'white',
              marginRight: '-1px',
              paddingRight: '1px',
              borderTop: '1px',
              borderTopStyle: 'solid',
              borderTopColor: '#eee',
            }}
          />
          <Flex
            flexDirection="row"
            style={{
              gap: '6px',
              borderBottomColor: '#fff',
              backgroundColor: '#fff',
              borderBottomStyle: 'none',
              borderBottom: 0,
              marginRight: '-1px',
              paddingRight: '1px',
              marginBottom: '-1px',
              paddingBottom: '1px',
            }}
            justifyContent="space-between"
          >
            <StyledButton
              style={{ flex: 1 }}
              data-test="print-account-statement-button"
              id="print-account-statement-button"
              size="small"
              onClick={handlePrintClick}
              noSpacing
              disabled={isFetchingOwedAmount}
            >
              <IconPrinters size="16px" />
              Print
            </StyledButton>
            <StyledButton
              style={{ flex: 1 }}
              data-test="email-account-statement-button"
              id="email-account-statement-button"
              size="small"
              variant="secondary"
              fill
              onClick={handleEmailClick}
              noSpacing
              disabled={isFetchingOwedAmount}
            >
              <IconMail size="16px" />
              Email
            </StyledButton>
          </Flex>
        </>
      )}
    </>
  );
}
